
import { defineComponent, PropType } from 'vue'
import { RepoPerms } from '~/types/permTypes'

import { RepositoryCollaboratorPermission, RepositoryKindChoices } from '~/types/types'

/**
 * Component for repository analysis settings.
 */
export default defineComponent({
  name: 'RepoAnalysisSettings',
  props: {
    branch: {
      type: String,
      required: true
    },
    enableGitMod: {
      type: Boolean,
      required: true
    },
    enableSarifReportInference: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    repoKind: {
      type: String as PropType<RepositoryKindChoices>,
      required: true
    },
    repoPermission: {
      type: String as PropType<RepositoryCollaboratorPermission>,
      required: true
    },
    selectedAnalysisScope: {
      type: String,
      required: true
    },
    vcsProviderText: {
      type: String,
      required: true
    },
    disableGroupManagedSettings: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'change-analysis-scope': (newVal: string) => true,
    'toggle-enable-git-mod': (newVal: boolean) => true,
    'toggle-sarif-report-inference': (newVal: boolean) => true,
    'update-branch': (newVal: string) => true
  },
  computed: {
    ANALYSIS_SCOPES() {
      return [
        {
          description:
            'Report issues only for modified lines in changed files in the pull request compared to the default branch.',
          includeDivider: true,
          label: 'BASELINE',
          value: 'granular'
        },
        {
          description:
            'Report all issues in all the changed files in the pull request. We do not recommend this as it can get noisy.',
          includeDivider: false,
          label: 'BROAD',
          value: 'broad'
        }
      ]
    },

    canChangeAnalysisScope(): boolean {
      return this.$gateKeeper.repo(RepoPerms.CHANGE_ANALYSIS_SCOPE, this.repoPermission)
    },

    canChangeDefaultAnalysisBranch(): boolean {
      return this.$gateKeeper.repo(RepoPerms.CHANGE_DEFAULT_ANALYSIS_BRANCH, this.repoPermission)
    },

    canToggleSubModulesEnabledStatus(): boolean {
      return this.$gateKeeper.repo(RepoPerms.TOGGLE_SUB_MODULES_ENABLED_STATUS, this.repoPermission)
    },

    canToggleReportInferenceStatus(): boolean {
      return this.$gateKeeper.repo(RepoPerms.TOGGLE_REPORT_INFERENCE, this.repoPermission)
    },

    isRegularRepository(): boolean {
      return this.repoKind === RepositoryKindChoices.Repo
    },

    isMonorepo(): boolean {
      return this.repoKind === RepositoryKindChoices.Monorepo
    },

    isSubRepository(): boolean {
      return this.repoKind === RepositoryKindChoices.Subrepo
    },

    gitSubmodulesAttrs() {
      return {
        props: {
          description:
            'Enable this if the repository depends on code inside submodules. If the submodules are private, ensure that DeepSource has access to them via the SSH key.',
          disabled: !this.canToggleSubModulesEnabledStatus,
          loading: this.loading,
          title: 'Use Git submodules',
          value: this.enableGitMod
        },
        handlers: {
          toggle: (newVal: boolean) => this.$emit('toggle-enable-git-mod', newVal)
        }
      }
    },

    analyzerReportInferenceAttrs() {
      return {
        props: {
          disabled:
            !this.canToggleReportInferenceStatus ||
            this.loading ||
            this.disableGroupManagedSettings,
          loading: this.loading,
          value: this.enableSarifReportInference
        },
        handlers: {
          toggle: (newVal: boolean) => this.$emit('toggle-sarif-report-inference', newVal)
        }
      }
    }
  }
})
